
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import AuthPageLayout from '@/components/auth/AuthPageLayout';
import PhoneInput from '@/components/auth/PhoneInput';
import StepIndicator from '@/components/auth/StepIndicator';
import { Country, DEFAULT_COUNTRY, isValidPhone, toE164 } from '@/lib/phone';
import { httpClient } from '@/lib/http-client';
import { getEndpointUrl } from '@/lib/api-config';

export default function RegisterPage() {
  const navigate = useNavigate();
  const { user, isLoading, register } = useAuth();

  const [step, setStep] = useState<1 | 2>(1);

  const [country, setCountry] = useState<Country>(DEFAULT_COUNTRY);
  const [phone, setPhone] = useState('');
  const [registrationToken, setRegistrationToken] = useState('');

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isLoading && user && user.emailVerified) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, isLoading, navigate]);

  const handlePhoneSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!isValidPhone(phone, country)) {
      setError('Ingresa un número de teléfono válido');
      return;
    }
    setSubmitting(true);
    try {
      if (import.meta.env.VITE_ENABLE_MOCK === 'true') {
        await new Promise((r) => setTimeout(r, 400));
        setRegistrationToken('mock-registration-token');
      } else {
        const url = getEndpointUrl('auth', 'register-phone');
        const response = await httpClient.post(url, {
          phone: toE164(phone, country),
          ...(registrationToken ? { registrationToken } : {}),
        });
        const data = await response.json();
        setRegistrationToken(data.registrationToken);
      }
      setStep(2);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'No pudimos validar el teléfono');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (firstName.trim().length < 2) {
      setError('El nombre debe tener al menos 2 caracteres');
      return;
    }
    if (lastName.trim().length < 2) {
      setError('El apellido debe tener al menos 2 caracteres');
      return;
    }
    if (password !== confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }
    if (password.length < 8 || password.length > 20) {
      setError('La contraseña debe tener entre 8 y 20 caracteres');
      return;
    }

    setSubmitting(true);
    try {
      await register(email, password, firstName.trim(), lastName.trim(), registrationToken);
      navigate('/verify-email');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Error al registrarse');
    } finally {
      setSubmitting(false);
    }
  };

  const leftProps =
    step === 1
      ? {
          leftIcon: 'smartphone',
          leftTitle: (
            <>
              Empecemos por
              <br />
              tu teléfono
            </>
          ),
          leftSubtitle:
            'Validaremos tu número antes de crear tu cuenta para proteger tu acceso.',
        }
      : {
          leftIcon: 'bolt',
          leftTitle: (
            <>
              Comienza a crecer
              <br />
              con FLUX
            </>
          ),
          leftSubtitle:
            'Únete a la plataforma financiera líder para comercios y lleva tu negocio al siguiente nivel.',
        };

  return (
    <AuthPageLayout {...leftProps}>
      <div className="absolute top-6 left-6 z-20">
        <Link
          to="/login"
          className="inline-flex items-center gap-2 text-sm text-slate-500 dark:text-gray-400 hover:text-primary transition-colors font-medium"
        >
          <span className="material-icons text-sm">arrow_back</span>
          Volver
        </Link>
      </div>

      <div className="flex-1 flex items-center justify-center p-4 sm:p-12 lg:p-24">
        <div className="w-full max-w-md space-y-8">
          <div className="flex md:hidden justify-center mb-6">
            <div className="flex items-center gap-2">
              <span className="material-icons text-primary text-3xl">account_balance_wallet</span>
              <span className="font-bold text-2xl tracking-tight text-gray-900 dark:text-white">FLUX</span>
            </div>
          </div>

          {step === 1 ? (
            <>
              <div className="text-left">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 bg-blue-50 dark:bg-blue-900/20 rounded-lg flex items-center justify-center md:hidden">
                    <span className="material-icons text-primary text-xl">smartphone</span>
                  </div>
                  <h2 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
                    Crea tu cuenta
                  </h2>
                </div>
                <p className="text-slate-500 dark:text-gray-400 mt-2">
                  Ingresa tu número de teléfono para comenzar.
                </p>
              </div>

              <StepIndicator current={1} total={2} />

              {error && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg px-4 py-3 text-sm text-red-700 dark:text-red-400 flex items-center gap-2">
                  <span className="material-icons text-base">error</span>
                  {error}
                </div>
              )}

              <form onSubmit={handlePhoneSubmit} className="mt-8 space-y-6">
                <div className="space-y-5">
                  <div>
                    <label
                      htmlFor="phone"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                    >
                      Teléfono
                    </label>
                    <PhoneInput
                      id="phone"
                      value={phone}
                      country={country}
                      onValueChange={setPhone}
                      onCountryChange={setCountry}
                      placeholder={country.exampleNational}
                      autoFocus
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Te enviaremos notificaciones importantes a este número.
                    </p>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-bold rounded-xl text-white bg-primary hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary shadow-lg shadow-blue-500/30 transition-all transform hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Validando...' : 'Continuar'}
                  <span className="absolute right-4 flex items-center">
                    <span className="material-icons text-blue-200 group-hover:text-white transition-colors text-sm">
                      arrow_forward
                    </span>
                  </span>
                </button>
              </form>

              <div className="mt-6">
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-200 dark:border-gray-700" />
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-white dark:bg-background-dark text-gray-500">
                      O también puedes
                    </span>
                  </div>
                </div>
                <div className="mt-6 text-center">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    ¿Ya tienes cuenta?
                    <Link
                      to="/login"
                      className="font-medium text-primary hover:text-blue-600 transition-colors ml-1"
                    >
                      Inicia sesión
                    </Link>
                  </p>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="text-left">
                <button
                  type="button"
                  onClick={() => { setStep(1); setError(''); }}
                  className="inline-flex items-center gap-1 text-sm text-slate-500 dark:text-gray-400 hover:text-primary transition-colors font-medium mb-2"
                >
                  <span className="material-icons text-sm">arrow_back</span>
                  Atrás
                </button>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 bg-blue-50 dark:bg-blue-900/20 rounded-lg flex items-center justify-center md:hidden">
                    <span className="material-icons text-primary text-xl">bolt</span>
                  </div>
                  <h2 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
                    Crea tu cuenta
                  </h2>
                </div>
                <p className="text-slate-500 dark:text-gray-400 mt-2">
                  Completa tus datos para terminar tu registro.
                </p>
              </div>

              <StepIndicator current={2} total={2} />

              {error && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg px-4 py-3 text-sm text-red-700 dark:text-red-400 flex items-center gap-2">
                  <span className="material-icons text-base">error</span>
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="mt-8 space-y-6">
                <div className="space-y-5">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label
                        htmlFor="firstName"
                        className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                      >
                        Nombre
                      </label>
                      <div className="mt-1 relative rounded-md shadow-sm">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <span className="material-symbols-outlined text-gray-400 text-[20px]">person</span>
                        </div>
                        <input
                          id="firstName"
                          name="firstName"
                          type="text"
                          autoComplete="given-name"
                          required
                          maxLength={50}
                          value={firstName}
                          onChange={(e) => setFirstName(e.target.value)}
                          placeholder="Juan"
                          className="block w-full pl-10 pr-3 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-primary focus:border-primary text-sm dark:bg-surface-dark dark:text-white placeholder-gray-400 transition-colors"
                        />
                      </div>
                    </div>
                    <div>
                      <label
                        htmlFor="lastName"
                        className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                      >
                        Apellido
                      </label>
                      <div className="mt-1 relative rounded-md shadow-sm">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <span className="material-symbols-outlined text-gray-400 text-[20px]">person</span>
                        </div>
                        <input
                          id="lastName"
                          name="lastName"
                          type="text"
                          autoComplete="family-name"
                          required
                          maxLength={50}
                          value={lastName}
                          onChange={(e) => setLastName(e.target.value)}
                          placeholder="Pérez"
                          className="block w-full pl-10 pr-3 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-primary focus:border-primary text-sm dark:bg-surface-dark dark:text-white placeholder-gray-400 transition-colors"
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="email"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                    >
                      Correo electrónico
                    </label>
                    <div className="mt-1 relative rounded-md shadow-sm">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <span className="material-symbols-outlined text-gray-400 text-[20px]">mail</span>
                      </div>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        autoComplete="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="nombre@empresa.com"
                        className="block w-full pl-10 pr-3 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-primary focus:border-primary text-sm dark:bg-surface-dark dark:text-white placeholder-gray-400 transition-colors"
                      />
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="password"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                    >
                      Contraseña
                    </label>
                    <div className="mt-1 relative rounded-md shadow-sm">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <span className="material-symbols-outlined text-gray-400 text-[20px]">lock</span>
                      </div>
                      <input
                        id="password"
                        name="password"
                        type={showPassword ? 'text' : 'password'}
                        autoComplete="new-password"
                        required
                        maxLength={20}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="••••••••"
                        className="block w-full pl-10 pr-10 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-primary focus:border-primary text-sm dark:bg-surface-dark dark:text-white placeholder-gray-400 transition-colors"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      >
                        <span className="material-symbols-outlined text-gray-400 text-[20px] hover:text-gray-600">
                          {showPassword ? 'visibility_off' : 'visibility'}
                        </span>
                      </button>
                    </div>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      La contraseña debe tener entre 8 y 20 caracteres
                    </p>
                  </div>

                  <div>
                    <label
                      htmlFor="confirm_password"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                    >
                      Confirmar contraseña
                    </label>
                    <div className="mt-1 relative rounded-md shadow-sm">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <span className="material-symbols-outlined text-gray-400 text-[20px]">lock_reset</span>
                      </div>
                      <input
                        id="confirm_password"
                        name="confirm_password"
                        type={showConfirm ? 'text' : 'password'}
                        autoComplete="new-password"
                        required
                        maxLength={20}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="••••••••"
                        className="block w-full pl-10 pr-10 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-primary focus:border-primary text-sm dark:bg-surface-dark dark:text-white placeholder-gray-400 transition-colors"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirm(!showConfirm)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      >
                        <span className="material-symbols-outlined text-gray-400 text-[20px] hover:text-gray-600">
                          {showConfirm ? 'visibility_off' : 'visibility'}
                        </span>
                      </button>
                    </div>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-bold rounded-xl text-white bg-primary hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary shadow-lg shadow-blue-500/30 transition-all transform hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Registrando...' : 'Continuar'}
                  <span className="absolute right-4 flex items-center">
                    <span className="material-icons text-blue-200 group-hover:text-white transition-colors text-sm">
                      arrow_forward
                    </span>
                  </span>
                </button>
              </form>

              <div className="mt-6">
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-200 dark:border-gray-700" />
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-white dark:bg-background-dark text-gray-500">
                      O también puedes
                    </span>
                  </div>
                </div>
                <div className="mt-6 text-center">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    ¿Ya tienes cuenta?
                    <Link
                      to="/login"
                      className="font-medium text-primary hover:text-blue-600 transition-colors ml-1"
                    >
                      Inicia sesión
                    </Link>
                  </p>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </AuthPageLayout>
  );
}
